<template>
  <page-template :is-loading="isLoading" :data-exists="selectedNamespaceData !== null">
    <template #pageHeadline>
      {{
        isNew
          ? $t('systemDB.configPage.newPageHeadline')
          : $t('systemDB.configPage.editPageHeadline')
      }}
    </template>
    <template #secondLevelHeaderLeft>
      <rbz-form-button
        variant="tertiary"
        icon="IconArrowBack"
        v-b-tooltip="$t('common.operations.returnToList')"
        link-route-name="System DB List"
        classes="redirect-to-list-button"
        data-qa="redirect-to-list-button"
      />
      <template v-if="selectedNamespaceData && !isNew">
        <div class="vertical-divider">
          <layout-divider-line orientation="vertical" color="light-gray" />
        </div>
        <rbz-dropdown
          v-if="selectedNamespace"
          :items="dropdownData.map((namespace) => [namespace.id, namespace.name])"
          :disabled="isLoading"
          v-model:value="selectedNamespace"
          @update:value="switchNamespace($event)"
          :ignore-update-in-process="true"
          type="single-select"
          :header-tooltip="$t('systemDB.configPage.switchTooltip')"
          classes="switch-namespace switch-page-entry"
          data-qa="switch-namespace"
        />
      </template>
    </template>
    <template #secondLevelHeaderRight>
      <template v-if="selectedNamespaceData && !isNew">
        <rbz-form-button
          :label="$t('common.operations.download.label')"
          variant="tertiary"
          icon="IconDownload"
          @button-click="downloadNamespace()"
          class="download-namespace-button"
          :disabled="isLoading"
          data-qa="download-namespace-button"
        />
        <rbz-form-button
          :label="$t('common.operations.delete.label')"
          variant="tertiary"
          icon="IconDelete"
          @button-click="openDeleteNamespaceModal()"
          :is-loading="isDeleteNamespaceLoading"
          class="delete-namespace-button"
          :disabled="isLoading || selectedNamespaceProtected"
          :tooltip="deleteTooltip"
          data-qa="delete-namespace-button"
        />
        <div class="vertical-divider">
          <layout-divider-line orientation="vertical" color="light-gray" />
        </div>
      </template>
      <rbz-form-button
        :label="$t('common.operations.save.label')"
        :disabled="isLoading || !isNamespaceValid"
        @button-click="saveChanges()"
        class="save-changes-button"
        data-qa="save-changes-button"
      />
    </template>
    <template #pageContent>
      <div class="rbz-field namespace-name-input">
        <rbz-form-input
          name="namespace-name-input"
          data-qa="namespace-name-input"
          :label="$t('systemDB.configPage.name')"
          :placeholder="$t('systemDB.configPage.name')"
          :disabled="isNamespaceProtected()"
          v-model:inputValue="namespaceNameInput"
          :is-invalid="!isSelectedNamespaceNewNameValid"
        />
      </div>
      <div class="rbz-field">
        <label class="rbz-label">
          {{ $t('systemDB.configPage.keys.title') }}
        </label>
        <rbz-table
          :columns="columns"
          :row-classes="
            (item) => {
              const keyNameValid = isKeyNameValid(item.name, item.id);
              const keyValueValid = isKeyValueValid(item.value, item.id);
              return !keyNameValid || !keyValueValid ? 'has-errors' : '';
            }
          "
          :table-data="selectedNamespaceKeys"
          :row-buttons="rowButtons"
          :show-new-as-row="true"
          :show-new-default-data="{ name: '' }"
          :show-download-button="false"
          :show-expander-column="true"
          :persist-expander-data="true"
          data-qa="keys-table"
          @add-inline-data="addNewKey"
          @delete-row-button-clicked="deleteKey"
        >
          <template #rowExpander="rowProps">
            <div class="rbz-field">
              <rbz-form-input
                name="key-name-input"
                data-qa="key-name-input"
                class="key-name-input"
                :label="$t('systemDB.configPage.keys.keyName')"
                :placeholder="$t('systemDB.configPage.keys.keyName')"
                :disabled="isKeyProtected(rowProps.row.id)"
                v-model:inputValue="rowProps.row.name"
                :is-invalid="!isKeyNameValid(rowProps.row.name, rowProps.row.id)"
              />
            </div>
            <div class="rbz-field json-editor-field">
              <rbz-json-editor
                :value="rowProps.row.value"
                :label="$t('systemDB.configPage.keys.keyValue')"
                v-model:input-value="rowProps.row.value"
                @invalid="isJsonValueInvalid(rowProps.row.id, $event)"
                @update:inputValue="rowProps.row.value = $event"
              >
              </rbz-json-editor>
            </div>
          </template>
        </rbz-table>
      </div>
      <rbz-modal-dialog
        :show="showDeleteNamespaceModal"
        title-icon="IconWarning"
        :title="$t('common.operations.delete.modal.title', entityName)"
        :primary-btn-title="$t('common.operations.delete.modal.confirm')"
        @onPrimaryBtnClick="deleteNamespace"
        :secondary-btn-title="$t('common.operations.delete.modal.cancel')"
        @onSecondaryBtnClick="showDeleteNamespaceModal = false"
        @closeModal="showDeleteNamespaceModal = false"
        size="medium"
      >
        <template #content>
          {{ $t('common.operations.delete.modal.message', { name: selectedNamespace }) }}
        </template>
      </rbz-modal-dialog>
    </template>
  </page-template>
</template>

<script lang="ts">
import _ from 'lodash';
import RequestsUtils from '@/helpers/RequestsUtils';
import { useRouting } from '@/helpers/RoutingUtils';
import Utils from 'ui-component-library/helpers/Utils';
import { defineComponent } from 'vue';
import { GenericObject, ServerGroup } from '@/types';
import PageTemplate from '@/views/PageTemplate.vue';
import RbzTable from 'ui-component-library/rbz/components/RbzTable.vue';
import RbzFormButton from '@/components/RbzFormButton.vue';
import LayoutDividerLine from 'ui-component-library/rbz/components/LayoutDividerLine.vue';
import { mapStores } from 'pinia';
import { useBranchesStore } from '@/store/BranchesStore';
import { usePlanetStore } from '@/store/PlanetStore';
import { useUserStore } from '@/store/UserStore';
import RbzDropdown from '@/components/RbzDropdown.vue';
import RbzFormInput from '@/components/RbzFormInput.vue';
import RbzModalDialog from '@/components/RbzModalDialog.vue';
import RbzJsonEditor from '@/components/RbzJsonEditor.vue';
import { RbzTableColumnOptions, RbzTableRowButton } from 'ui-component-library/rbz/types';

type NamespaceKeysTableData = {
  id: string;
  name: string;
  value: string;
};

export default defineComponent({
  name: 'SystemDBEditor',
  setup() {
    const { getRouteName, getListPath, navigateTo, normalizeRouteName } = useRouting();
    return { getRouteName, getListPath, navigateTo, normalizeRouteName };
  },
  components: {
    RbzFormButton,
    RbzFormInput,
    LayoutDividerLine,
    PageTemplate,
    RbzDropdown,
    RbzModalDialog,
    RbzTable,
    RbzJsonEditor,
  },
  data() {
    return {
      columns: [
        {
          id: 'name',
          title: this.$t('systemDB.configPage.keys.keyName'),
          fieldNames: ['name'],
          isSearchable: true,
          validate: this.validateKeyName,
          cellContentClasses: 'ellipsis',
        },
      ] as RbzTableColumnOptions[],
      rowButtons: [
        {
          id: 'delete',
          icon: 'IconDelete',
          disabledCallback: (key: GenericObject) => {
            return this.isKeyProtected(key.id);
          },
          tooltipCallback: (key: GenericObject) => {
            if (this.isKeyProtected(key.id)) {
              return this.$t('common.operations.delete.disabledTooltip.protected', {
                plural: this.$t('systemDB.configPage.keys.plural'),
                singular: this.$t('systemDB.configPage.keys.singular'),
              });
            }
            return this.$t('common.operations.delete.tableTooltip');
          },
        },
      ] as RbzTableRowButton[],
      dropdownData: [],
      selectedNamespace: null as string,
      selectedNamespaceData: null as GenericObject,
      originalNamespaceData: null as GenericObject,
      selectedNamespaceOriginalKeys: null as string[],
      selectedNamespaceKeys: null as NamespaceKeysTableData[],
      namespaceNameInput: '',
      pageLoadingCounter: null,
      isDeleteNamespaceLoading: false,
      showDeleteNamespaceModal: false,
      protectedNames: {
        system: {
          namespace: true,
          keys: ['publishinfo', 'tags', 'user', 'feature-toggle', 'log-exporter'],
        },
      } as GenericObject,
      invalidKeyValues: [],
      serverGroups: null,
      featureToggleKey: 'feature-toggle',
      isNew: false,
    };
  },
  watch: {
    selectedNamespaceKeys: {
      handler: async function (val: NamespaceKeysTableData[]) {
        const newNamespaceValue: GenericObject = {};
        _.forEach(val, (keyItem) => {
          newNamespaceValue[keyItem.name] = JSON.parse(keyItem.value);
        });
        if (!_.isEqual(this.selectedNamespaceData, newNamespaceValue)) {
          this.selectedNamespaceData = newNamespaceValue;
        }
      },
      deep: true,
      immediate: true,
    },
  },
  computed: {
    selectedBranch(): string {
      return this.branchesStore.selectedBranchId;
    },

    ...mapStores(useBranchesStore, usePlanetStore, useUserStore),

    userPlanet(): string {
      return this.userStore.getUserPlanet();
    },

    entityName() {
      return {
        plural: this.$t('systemDB.plural'),
        singular: this.$t('systemDB.singular'),
      };
    },

    isLoading(): boolean {
      return this.pageLoadingCounter === null || this.pageLoadingCounter > 0;
    },

    entityNotSaved(): boolean {
      return !_.isEqual(this.selectedNamespaceData, this.originalNamespaceData);
    },

    selectedNamespaceProtected(): boolean {
      return this.selectedNamespace === 'system';
    },

    deleteTooltip(): string {
      if (this.selectedNamespaceProtected) {
        return this.$t('common.operations.delete.disabledTooltip.protected', this.entityName);
      }
      return '';
    },

    isSelectedNamespaceNewNameValid(): boolean {
      const newName = this.namespaceNameInput.trim();
      const isNamespaceNameEmpty = newName === '';
      const isNamespaceNameDuplicate = _.some(this.dropdownData, (item: GenericObject) => {
        return item.id === newName;
      })
        ? this.selectedNamespace !== newName
        : false;
      return !isNamespaceNameEmpty && !isNamespaceNameDuplicate;
    },

    isNamespaceValid(): boolean {
      const namespaceNameValid = this.isSelectedNamespaceNewNameValid;
      const keyNamesValid = !_.some(this.selectedNamespaceKeys, (keyItem) => {
        return !this.isKeyNameValid(keyItem.name, keyItem.id);
      });
      const keyValuesValid = !_.some(this.selectedNamespaceKeys, (keyItem) => {
        return !this.isKeyValueValid(keyItem.value, keyItem.id);
      });
      return namespaceNameValid && keyNamesValid && keyValuesValid;
    },
  },
  methods: {
    setPageLoadingStatus(isLoading: boolean) {
      if (isLoading) {
        this.pageLoadingCounter++;
      } else {
        this.pageLoadingCounter--;
      }
    },

    redirectToList() {
      this.navigateTo(this.getListPath('system-db'));
    },

    switchNamespace(namespace: string) {
      this.$router.push(`/${this.selectedBranch}/system-db/config/${namespace}`);
    },

    async loadDropdownData() {
      this.setPageLoadingStatus(true);
      const response = await RequestsUtils.sendRequest({
        host: this.userPlanet,
        methodName: 'GET',
        url: 'db',
      });
      this.dropdownData = _.map(response?.data?.items?.sort(), (namespace: string) => {
        return {
          id: namespace,
          name: namespace,
        };
      });
      this.setPageLoadingStatus(false);
    },

    async loadNamespace(namespace: string) {
      this.setPageLoadingStatus(true);
      this.selectedNamespace = namespace;
      this.namespaceNameInput = this.selectedNamespace.trim();
      const response = await RequestsUtils.sendRequest({
        host: this.userPlanet,
        methodName: 'GET',
        url: `db/${this.selectedNamespace}`,
      });
      this.selectedNamespaceData = response?.data || {};
      this.originalNamespaceData = _.cloneDeep(this.selectedNamespaceData);
      this.selectedNamespaceOriginalKeys = Object.keys(this.selectedNamespaceData);
      this.selectedNamespaceKeys = Object.keys(this.selectedNamespaceData).map((key) => {
        return {
          id: key,
          name: key,
          value: JSON.stringify(this.selectedNamespaceData[key]),
        };
      });
      this.setPageLoadingStatus(false);
    },

    downloadNamespace() {
      Utils.downloadFile(this.selectedNamespace, 'json', this.selectedNamespaceData);
    },

    openDeleteNamespaceModal() {
      this.showDeleteNamespaceModal = true;
    },

    async deleteNamespace(nameChange?: boolean) {
      if (!nameChange) {
        this.isDeleteNamespaceLoading = true;
      }
      const namespace = this.selectedNamespace;
      let successMessage;
      let failureMessage;
      if (!nameChange) {
        successMessage = this.$t('common.operations.delete.messages.success', this.entityName);
        failureMessage = this.$t('common.operations.delete.messages.failure', this.entityName);
      }
      await RequestsUtils.sendRequest({
        host: this.userPlanet,
        methodName: 'DELETE',
        url: `db/${namespace}`,
        successMessage,
        failureMessage,
      });
      const dropdownIndex = _.findIndex(this.dropdownData, (item) => {
        return item.id === namespace;
      });
      this.dropdownData.splice(dropdownIndex, 1);
      this.selectedNamespace = null;
      if (!nameChange) {
        this.redirectToList();
        this.isDeleteNamespaceLoading = false;
      }
    },

    async loadServerGroups() {
      const url = `conf/${this.selectedBranch}/server-groups`;
      const response = await RequestsUtils.sendRequest({
        methodName: 'GET',
        host: this.userPlanet,
        url,
      });
      this.serverGroups = response?.data?.items || [];
    },

    async updateServerGroupConfig() {
      const featureToggleConfig = this.selectedNamespaceKeys.find((key) => {
        return key.id === this.featureToggleKey;
      });
      if (
        !this.serverGroups ||
        !featureToggleConfig.value?.['ssl-client-to-v5-client-ca-certificate']
      ) {
        return;
      }
      const updates = this.serverGroups.map((serverGroup: ServerGroup) => {
        const url = `conf/${this.selectedBranch}/server-groups/${serverGroup.id}`;
        serverGroup['client_certificate'] = '';
        serverGroup['client_certificate_mode'] = 'off';
        return RequestsUtils.sendRequest({
          host: this.userPlanet,
          methodName: 'PUT',
          url,
          data: serverGroup,
        });
      });
      await Promise.all(updates);
    },

    async saveChanges() {
      this.setPageLoadingStatus(true);
      const namespaceName = this.namespaceNameInput.trim();
      let successMessage;
      let failureMessage;
      this.updateServerGroupConfig();
      if (this.isNew) {
        successMessage = this.$t('common.operations.new.messages.success', this.entityName);
        failureMessage = this.$t('common.operations.new.messages.failure', this.entityName);
      } else {
        successMessage = this.$t('common.operations.save.messages.success', this.entityName);
        failureMessage = this.$t('common.operations.save.messages.failure', this.entityName);
      }
      if (this.selectedNamespace === namespaceName) {
        // If namespace name did not change - save normally
        await RequestsUtils.sendRequest({
          host: this.userPlanet,
          methodName: 'PUT',
          url: `db/${namespaceName}`,
          data: this.selectedNamespaceData,
          successMessage,
          failureMessage,
          displayServerFailureMessage: true,
        });
        _.forEach(this.selectedNamespaceOriginalKeys, (originalKey: string) => {
          if (!Object.keys(this.selectedNamespaceData).includes(originalKey)) {
            RequestsUtils.sendRequest({
              host: this.userPlanet,
              methodName: 'DELETE',
              url: `db/${namespaceName}/${originalKey}`,
            });
          }
        });
        this.originalNamespaceData = _.cloneDeep(this.selectedNamespaceData);
      } else {
        // If namespace name changed -> Save the data under the new name and remove the old namespace
        const response = await RequestsUtils.sendRequest({
          host: this.userPlanet,
          methodName: 'POST',
          url: `db/${namespaceName}`,
          data: this.selectedNamespaceData,
          successMessage,
          failureMessage,
          displayServerFailureMessage: true,
        });
        if (response) {
          if (this.isNew) {
            const currentRoute = this.$route.path;
            const targetRoute = currentRoute.replace('new', `config/${namespaceName}`);
            this.$router.push(targetRoute);
          } else {
            await this.deleteNamespace(true);
            this.switchNamespace(namespaceName);
            this.originalNamespaceData = _.cloneDeep(this.selectedNamespaceData);
          }
        }
      }
      // Reset user idle tracker in order to use the new configuration
      if (this.selectedNamespace === 'system') {
        const userStore = useUserStore();
        userStore.idleTrackerStop();
        userStore.idleTrackerStart();
      }
      this.selectedNamespaceOriginalKeys = Object.keys(this.selectedNamespaceData);
      this.setPageLoadingStatus(false);
    },

    async addNewKey(newKeyData: GenericObject) {
      const newKey = newKeyData.name;
      if (newKey && !_.map(this.selectedNamespaceKeys, 'id').includes(newKey)) {
        const newValue = {};
        this.selectedNamespaceKeys.unshift({
          id: newKey,
          name: newKey,
          value: JSON.stringify(newValue),
        });
      }
    },

    async deleteKey(keyItem: NamespaceKeysTableData) {
      const key: string = keyItem.id;
      const keyIndex = _.findIndex(this.selectedNamespaceKeys, (keyItem) => {
        return keyItem.id === key;
      });
      this.selectedNamespaceKeys.splice(keyIndex, 1);
    },

    isKeyNameValid(key: string, originalKey: string) {
      const isKeyNameEmpty = key === '';
      const isKeyNameDuplicate = _.some(this.selectedNamespaceKeys, (keyItem) => {
        return keyItem.id === key;
      })
        ? originalKey !== key
        : false;
      return !isKeyNameEmpty && !isKeyNameDuplicate;
    },

    isJsonValueInvalid(key: string, invalid: boolean) {
      const keyExists = this.invalidKeyValues.includes(key);
      if (invalid && !keyExists) {
        this.invalidKeyValues.push(key);
      }
      if (!invalid && keyExists) {
        this.invalidKeyValues = this.invalidKeyValues.filter((item) => item !== key);
      }
    },

    isKeyValueValid(value: string, id: string) {
      try {
        JSON.parse(value);
        return !this.invalidKeyValues.includes(id);
      } catch (err) {
        return false;
      }
    },

    isNamespaceProtected(): boolean {
      return this.protectedNames[this.selectedNamespace]?.namespace;
    },

    isKeyProtected(key: string): boolean {
      return this.protectedNames[this.selectedNamespace]?.keys?.includes(key);
    },

    validateKeyName(newKeyData: GenericObject): boolean {
      return !_.some(this.selectedNamespaceKeys, (keyItem) => {
        return keyItem.name === newKeyData.name;
      });
    },

    setSessionDataWatcher() {
      this.$watch(
        () => [this.selectedNamespaceData, this.entityNotSaved],
        (params) => {
          const [formData, entityNotSaved] = params;
          Utils.saveSessionData(formData, entityNotSaved);
        },
        {
          deep: true,
          immediate: true,
        }
      );
    },
  },
  created() {
    this.$watch(
      () => [this.$route.params?.['branch'], this.$route.params?.['namespace_id']],
      async (routeParams) => {
        const [_newBranch, newId] = routeParams;
        const routeName: string = this.normalizeRouteName(this.$route.name as string);
        if (['System DB Editor', 'New System DB'].includes(routeName)) {
          this.setPageLoadingStatus(true);
          this.isNew = routeName.startsWith('New');
          await this.branchesStore.getBranches();
          await this.loadDropdownData();
          if (this.isNew) {
            this.selectedNamespaceData = {};
            this.namespaceNameInput = Utils.generateUniqueEntityName(
              'New Namespace',
              _.map(this.dropdownData, 'id')
            );
            this.selectedNamespaceOriginalKeys = Object.keys(this.selectedNamespaceData);
            this.selectedNamespaceKeys = Object.keys(this.selectedNamespaceData).map((key) => {
              return {
                id: key,
                name: key,
                value: JSON.stringify(this.selectedNamespaceData[key]),
              };
            });
            this.originalNamespaceData = _.cloneDeep(this.selectedNamespaceData);
          } else {
            const namespace = newId as string;
            const dataExists = this.dropdownData.some((item) => {
              return item.id === namespace;
            });
            if (dataExists) {
              await this.loadNamespace(namespace);
              const featureToggleConfig = this.selectedNamespaceKeys.find((key) => {
                return key.id === this.featureToggleKey;
              });
              if (featureToggleConfig?.value?.['ssl-client-to-v5-client-ca-certificate']) {
                await this.loadServerGroups();
              }
            } else {
              this.redirectToList();
            }
          }
          const savedSessionData = sessionStorage.getItem('formData');
          if (savedSessionData) {
            this.selectedNamespaceData = JSON.parse(savedSessionData);
          }
          this.setSessionDataWatcher();
          this.setPageLoadingStatus(false);
        }
      },
      {
        deep: true,
        immediate: true,
      }
    );
  },
  beforeRouteLeave() {
    Utils.clearSessionData();
  },
});
</script>
<style scoped lang="scss">
@use 'ui-component-library/assets/styles/colors' as colors;
@use 'sass:map';

:deep(.rbz-table .has-errors) {
  .data-cell {
    color: map.get(colors.$colors, 'cinnabar');
  }
}
</style>
